/**
 * Size limits for spreadsheet imports (CSV / XLSX), shared by the server
 * parser and the browser wizard so both refuse the same files.
 *
 * A sheet's declared dimensions come from its farthest cell, so a tiny file
 * can claim millions of cells; and every parsed row becomes a record keyed by
 * every column. The parsers therefore visit only cells that exist and stop
 * with a clear error — never silently dropping data — as soon as a file
 * passes one of these limits.
 */

/** Columns in the header row (its farthest filled cell). */
export const IMPORT_FILE_MAX_COLUMNS = 500
/**
 * Data rows with a value, across every sheet read (blank lines and lines of
 * bare delimiters do not count).
 */
export const IMPORT_FILE_MAX_ROWS = 100_000
/** Columns × data rows, across every sheet read: the parsed table's size. */
export const IMPORT_FILE_MAX_CELLS = 5_000_000
/** The uploaded file itself. */
export const IMPORT_FILE_MAX_BYTES = 50 * 1024 * 1024
/** One CSV field, in characters (each field on its own). */
export const IMPORT_FILE_MAX_FIELD_CHARS = 1_000_000
/**
 * XLSX is a zip: its entries, and their total size once decompressed. The
 * size is set from the table limits: 5M cells at up to ~150 bytes of sheet
 * XML each (a 100k × 50 sheet of unique text is ~320 MB), so any workbook
 * within the row and cell limits fits, while a zip bomb stops here.
 */
export const IMPORT_XLSX_MAX_ENTRIES = 10_000
export const IMPORT_XLSX_MAX_UNCOMPRESSED_BYTES = 768 * 1024 * 1024

const formatCount = (value: number) => value.toLocaleString('en-US')

export type ImportFileLimitKind =
  | 'columns'
  | 'rows'
  | 'cells'
  | 'bytes'
  | 'field'
  | 'archive'
  | 'xlsx-csv'
  | 'xlsx-split'

/** Thrown when a file is too large to import; the message is user-facing. */
export class ImportFileTooLargeError extends Error {
  readonly kind: ImportFileLimitKind

  constructor(kind: ImportFileLimitKind, actual?: number) {
    super(importFileLimitMessage(kind, actual))
    this.name = 'ImportFileTooLargeError'
    this.kind = kind
  }
}

export function importFileLimitMessage(
  kind: ImportFileLimitKind,
  actual?: number,
): string {
  switch (kind) {
    case 'columns':
      return `This file has ${
        actual === undefined
          ? `more than ${formatCount(IMPORT_FILE_MAX_COLUMNS)}`
          : formatCount(actual)
      } columns. Imports read up to ${formatCount(IMPORT_FILE_MAX_COLUMNS)} columns. Remove the columns you don't need, then upload it again.`
    case 'rows':
      return `This file has more than ${formatCount(IMPORT_FILE_MAX_ROWS)} rows. Split it into smaller files and import them one at a time.`
    case 'cells':
      return `This file is too large to import in one go (more than ${formatCount(IMPORT_FILE_MAX_CELLS)} cells). Remove columns you don't need or split the rows into smaller files.`
    case 'bytes':
      return `This file is larger than ${IMPORT_FILE_MAX_BYTES / 1024 / 1024} MB. Split it into smaller files and import them one at a time.`
    case 'field':
      return `A single cell in this file holds more than ${formatCount(IMPORT_FILE_MAX_FIELD_CHARS)} characters. Check the file was exported as a normal CSV, then upload it again.`
    case 'xlsx-csv':
      return `This spreadsheet is too large to import as an Excel file. Save it as CSV and upload that instead (CSV imports support up to ${formatCount(IMPORT_FILE_MAX_ROWS)} rows).`
    case 'xlsx-split':
      return `This spreadsheet is too large to import as an Excel file. Split it into smaller files and import them one at a time.`
    case 'archive':
      return `This spreadsheet is too large to import once opened (more sheet data than an import can hold). Split it into smaller files and import them one at a time.`
  }
}

/**
 * Cumulative row / cell counting across every sheet a parse reads, so a
 * workbook cannot pass the limits by spreading rows over several sheets.
 */
export class ImportLimitBudget {
  private rows = 0
  private cells = 0

  /** One more data row of `columnCount` columns; throws past a limit. */
  addRow(columnCount: number): void {
    if (columnCount > IMPORT_FILE_MAX_COLUMNS)
      throw new ImportFileTooLargeError('columns', columnCount)
    this.rows += 1
    this.cells += columnCount
    if (this.rows > IMPORT_FILE_MAX_ROWS)
      throw new ImportFileTooLargeError('rows')
    if (this.cells > IMPORT_FILE_MAX_CELLS)
      throw new ImportFileTooLargeError('cells')
  }
}

/**
 * Counts data rows as a parser produces them and throws as soon as the row
 * or cell limit is passed, so no parser materializes an oversized table.
 */
export function assertImportTableWithinLimits(
  columnCount: number,
  rowCount: number,
): void {
  if (columnCount > IMPORT_FILE_MAX_COLUMNS)
    throw new ImportFileTooLargeError('columns', columnCount)
  if (rowCount > IMPORT_FILE_MAX_ROWS) throw new ImportFileTooLargeError('rows')
  if (columnCount * rowCount > IMPORT_FILE_MAX_CELLS)
    throw new ImportFileTooLargeError('cells')
}
